import { DistCheckSchema, ProductAdditionRespSchema } from "@lib/schemas";
import { ModalMessage } from "@lib/types";
import _ from "lodash";
import { z } from "zod";

const CACHED_DIST_CHECK_LOCAL_STORAGE_KEY = "cached-dist-checks";
const LIMIT_CACHED_DIST_CHECKS = 20;

interface TCachedDistCheck {
  soid: number;
  product_additions: z.infer<typeof DistCheckSchema>["product_additions"];
  outputModalMessages: ModalMessage[];
}
type TProductAddition = z.infer<typeof ProductAdditionRespSchema>;

type TIsHasDataChanged = boolean;
type TCachedOutputMessages = ModalMessage[];

export function hasDistDataChanged(
  soid: number,
  newProductAdditions: TProductAddition[],
  newOutputModalMessages: ModalMessage[]
): [TIsHasDataChanged, TCachedOutputMessages] {
  const cachedChecks = getCachedDistChecks();
  const cachedCheck = cachedChecks.find((check) => check.soid === soid);

  if (cachedCheck === undefined) {
    console.warn("Cached dist check is undefined for SOID:", soid);
    cacheProductAdditions(soid, newProductAdditions, newOutputModalMessages);

    return [true, []];
  }

  if (!_.isEqual(cachedCheck.product_additions, newProductAdditions)) {
    console.warn("Cached product additions do not match for SOID:", soid);
    cacheProductAdditions(soid, newProductAdditions, newOutputModalMessages);

    return [true, []];
  }

  return [false, cachedCheck.outputModalMessages];
}

function cacheProductAdditions(
  soid: number,
  newProductAdditions: TProductAddition[],
  newOutputModalMessages: ModalMessage[]
): void {
  const cachedChecks = getCachedDistChecks();
  const existingDistCheck = cachedChecks.find((check) => check.soid === soid);

  if (existingDistCheck === undefined) {
    // Add new dist check to cache
    cachedChecks.push({
      soid,
      product_additions: newProductAdditions,
      outputModalMessages: newOutputModalMessages,
    });
  } else {
    // Update existing cached check
    existingDistCheck.product_additions = newProductAdditions;
    existingDistCheck.outputModalMessages = newOutputModalMessages;
  }

  try {
    localStorage.setItem(
      CACHED_DIST_CHECK_LOCAL_STORAGE_KEY,
      JSON.stringify(cachedChecks.slice(-LIMIT_CACHED_DIST_CHECKS))
    );
  } catch (error) {
    console.warn("Failed to save to localStorage:", error);
  }
}

function getCachedDistChecks(): TCachedDistCheck[] {
  try {
    const cachedChecksString = localStorage.getItem(CACHED_DIST_CHECK_LOCAL_STORAGE_KEY);
    if (cachedChecksString === null) return [];

    const cachedChecks = JSON.parse(cachedChecksString) as TCachedDistCheck[];
    return cachedChecks;
  } catch (error) {
    console.warn("Error reading cached dist checks:", error);
    localStorage.removeItem(CACHED_DIST_CHECK_LOCAL_STORAGE_KEY);
    return [];
  }
}
